/**
 * What a credit-box ratio check was computed on (SCR-01). Kept apart from the
 * screening components so both the dossier panel and the intake panel read it.
 */
import type { ScreenCheck, ScreenRun } from '@/services/ingestion'

/** Which screen input each ratio check is computed on. */
const CHECK_INPUT: Record<string, string> = {
  max_ltv: 'stabilized_value',
  min_debt_yield_stabilized: 'stabilized_noi_or_ncf',
  min_dscr_amortizing: 'stabilized_noi_or_ncf',
}

/** The figure the screen took for its "stabilized" input, as a reader says it.
 *  Record keys from intake and catalogue keys from a re-screen both appear. */
const BASIS_LABEL: Record<string, string> = {
  value_stabilized: 'stabilized value',
  value_arep_untrended: 'untrended stabilized value',
  value_untrended: 'untrended stabilized value',
  value_appraised: 'as-is value',
  value_inplace: 'as-is value',
  property_value: 'as-is value',
  purchase_price: 'purchase price',
  ncf_stabilized: 'stabilized NCF',
  noi_stabilized: 'stabilized NOI',
  ncf_arep_untrended: 'untrended stabilized NCF',
  ncf_untrended: 'untrended stabilized NCF',
  ncf: 'NCF',
  noi: 'NOI',
  ncf_inplace: 'in-place NCF',
  noi_inplace: 'in-place NOI',
}
const STABILIZED_BASES = new Set([
  'value_stabilized', 'value_arep_untrended', 'value_untrended',
  'ncf_stabilized', 'noi_stabilized', 'ncf_arep_untrended', 'ncf_untrended',
])

/**
 * What a ratio check was computed on, and — when the screen's "stabilized"
 * input is a stand-in (the purchase price, an as-is value, an in-place NOI) —
 * that it is not a stabilized figure. The credit box falls back to these where
 * no stabilized figure is stated (its selection is disclosed on the stored
 * result), but the panel printed "PASS Maximum LTV 0.75 vs max 0.8" on
 * Bellweather's loan / purchase price with nothing to say so (SCR-01).
 */
export function checkBasis(check: ScreenCheck, run: ScreenRun): string | null {
  if (check.outcome === 'MISSING_INPUT') return null
  const field = CHECK_INPUT[check.name]
  const input = field ? (run.inputs ?? []).find((row) => row.field === field) : undefined
  if (!input || typeof input.value !== 'number' || !Number.isFinite(input.value)) return null
  const key = String(input.selectedFrom ?? '')
  const label = BASIS_LABEL[key] ?? (key || field).replace(/_/g, ' ')
  const figure = `$${Math.round(input.value).toLocaleString('en-US')}`
  const standIn = key && !STABILIZED_BASES.has(key)
    ? ` — not a stabilized ${field === 'stabilized_value' ? 'value' : 'figure'}; the package states none`
    : ''
  return `on the ${label} ${figure}${standIn}`
}

/**
 * A check's stored detail with its ratios rounded to four decimals. The credit
 * box writes the computed ratio as a raw float, so Bellweather's panel read
 * "0.08177777777777778 vs min 0.08"; results already stored keep that text, so
 * it is tidied where it is shown. Figures of four decimals or fewer, and
 * grouped amounts ("22,500,000"), pass through unchanged.
 */
export function checkDetail(detail: string | null | undefined): string {
  return String(detail ?? '').replace(/(?<![\d,.])\d+\.\d{5,}(?![\d,.])/g, (figure) =>
    String(Number(Number(figure).toFixed(4))),
  )
}
